//------------------------------------------------------------------------------
// Copyright (C) 2022- Cursor Insight Ltd.
//
// All rights reserved.
//------------------------------------------------------------------------------
// HTTP transport for Cursor Insight's ligthweight mouse tracker.
//------------------------------------------------------------------------------

import simpleAutoBind from './simpleAutoBind';
import { strToU8, zlibSync } from 'fflate';

import {
  DEFAULT_TRAP_ENABLE_COMPRESSION,
  DEFAULT_TRAP_SERVER_URL,
  SCHEMA,
} from './constants';

class HTTP {
  constructor(metadata, buffer) {
    simpleAutoBind(this);

    // Metadata reference
    this._metadata = metadata;

    // Buffer reference
    this._buffer = buffer;

    // Current sequence number that is sent over the wire
    this._sequenceNumber = 0;

    // Enable compressed, base64 encoded JSON data submission.  When `false`
    // only `JSON.stringify` is applied.
    this._enableCompression = DEFAULT_TRAP_ENABLE_COMPRESSION;

    // Set default URL.
    this._url = DEFAULT_TRAP_SERVER_URL;
  }

  // `enableCompression` setter
  set enableCompression(enableCompression) {
    this._enableCompression = !!enableCompression;
  }

  // `url` setter
  set url(url) {
    this._url = (typeof url === 'string') ? url : DEFAULT_TRAP_SERVER_URL;
  }

  // Submit data to remote Trap server
  async submit(buffer) {
    // Set up monotonically incrementing sequence number
    //
    // eslint-disable-next-line no-plusplus
    const sequenceNumber = this._sequenceNumber++;

    // Set up data to be sent over the wire
    const data = {
      schema: SCHEMA,
      metadata: {
        epoch: {
          value: this._buffer.epoch,
          unit: 'millisecond',
        },
        sessionId: this._metadata.sessionId,
        streamId: this._metadata.streamId,
        sequenceNumber,
        platform: this._metadata.platform,
        location: this._metadata.location,
        custom: this._metadata.custom,
        screen: this._metadata.screen,
        document: this._metadata.document,
      },
      chunk: buffer,
    };

    // Compress data if `enableCompression` is set
    const final = this._enableCompression
      ? zlibSync(strToU8(JSON.stringify(data)), { level: 9, mem: 10 })
      : JSON.stringify(data);

    // Set up content-type and its optional arguments: API key and envelope
    // encoding
    let contentType = 'text/plain';
    if (typeof this._metadata.apiKeyName === 'string'
      && typeof this._metadata.apiKeyValue === 'string') {
      contentType += '; ' +
        `${this._metadata.apiKeyName}=` +
        `${this._metadata.apiKeyValue}`;
    }
    contentType += `; encoding=json${this._enableCompression
      ? '+zlib'
      : ''}`;

    // Send data to a Trap server
    await fetch(this._url, {
      // *GET, POST, PUT, DELETE, etc.
      method: 'POST',

      // no-cors, *cors, same-origin
      mode: 'cors',

      // *default, no-cache, reload, force-cache, only-if-cached
      cache: 'no-cache',

      // include, *same-origin, omit
      credentials: 'omit',

      // necessary HTTP headers
      headers: {
        // No other headers are allowed in a preflight-less CORS request
        // For details: https://developer.mozilla.org/en-US/docs/Glossary/CORS-safelisted_request_header
        'Content-Type': contentType,
      },

      // manual, *follow, error
      redirect: 'follow',

      // no-referrer, *no-referrer-when-downgrade, origin,
      // origin-when-cross-origin, same-origin, strict-origin,
      // strict-origin-when-cross-origin, unsafe-url
      referrerPolicy: 'origin',

      // data to be sent
      body: final,
    });
  }
}

// No default export, just the class
export { HTTP };
